import { useEffect, useState } from "react";
import { Formik } from "formik";
import { Row, Col } from "react-bootstrap";
import ReactQuill from "react-quill-new";
import { useNavigate } from "react-router-dom";
import Breadcrumbs from "../../../components/Breadcrumbs";
import Button from "../../../components/button";
import FileUpload from "../../../components/FileUpload";
import Input from "../../../components/input";
import Select from "../../../components/Select";
import { apiService } from "../../../services/apiService";
import "../../../styles/Access.css";
import "../../../styles/AssetsRegistration.css";
import "../../../styles/DriverSafety.css";
import "../../../styles/Input.css";
import "../../../styles/Trip.css";
import "../../../styles/TripCostEstimation.css";
import { formats, modules } from "../../../utils/helper";
import { errorNotify, successNotify } from "../../../utils/toast";
import { createTripCostEstimationSchema } from "./createTripCostEstimation.schema";

const getTripCostEstimationInitialValues = () => ({
  tripId: "",
  tripLabel: "",
  tripRecordId: "",
  tripCode: "",
  vehicleId: "",
  driverId: "",
  driverName: "",
  driverImage: "",
  routeId: "",
  routeLabel: "",
  route: "",
  distance: "",
  fuelPricePerLiter: "",
  vehicleFuelEfficiency: "",
  foodAllowancePerDay: "",
  tripDuration: "",
  maintenanceCostPerKm: "",
  estimatedTollCost: "",
  estimatedRevenue: "",
  attachments: [],
  notes: "",
});

const formatTripCostEstimationDisplayRoute = (value = "") =>
  String(value || "")
    .replace(/\s*(?:->|>)\s*/g, " -> ")
    .replace(/\s*->\s*/g, " \u2192 ")
    .trim();

const formatTripCostEstimationTableRoute = (value = "") =>
  String(value || "")
    .replace(/\s*(?:->|\u2192)\s*/g, " > ")
    .trim();

const toPositiveNumber = (value, fallback = 0) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

const resolveTripDurationMultiplier = (value) => {
  const parsedValue = Number(value);

  if (Number.isFinite(parsedValue) && parsedValue > 0) {
    return parsedValue;
  }

  return String(value || "").trim() ? 1 : 0;
};

const buildTripCostEstimationMetrics = (values = {}) => {
  const distance = toPositiveNumber(values.distance);
  const fuelPricePerLiter = toPositiveNumber(values.fuelPricePerLiter);
  const vehicleFuelEfficiency = toPositiveNumber(values.vehicleFuelEfficiency);
  const foodAllowancePerDay = toPositiveNumber(values.foodAllowancePerDay);
  const tripDurationMultiplier = resolveTripDurationMultiplier(values.tripDuration);
  const maintenanceCostPerKm = toPositiveNumber(values.maintenanceCostPerKm);
  const estimatedTollCost = toPositiveNumber(values.estimatedTollCost);
  const estimatedRevenue = toPositiveNumber(values.estimatedRevenue);

  const estimatedFuelCost =
    vehicleFuelEfficiency > 0
      ? Math.round((distance / vehicleFuelEfficiency) * fuelPricePerLiter)
      : 0;
  const estimatedFoodCost = Math.round(foodAllowancePerDay * tripDurationMultiplier);
  const estimatedMaintenanceCost = Math.round(maintenanceCostPerKm * distance);
  const estimatedCost = Math.round(
    estimatedFuelCost +
      estimatedFoodCost +
      estimatedMaintenanceCost +
      estimatedTollCost
  );
  const projectedProfit = Math.round(estimatedRevenue - estimatedCost);

  return {
    estimatedFuelCost,
    estimatedFoodCost,
    estimatedMaintenanceCost,
    estimatedCost,
    projectedProfit,
  };
};

const renderFieldLabel = (label, required = true) => (
  <span className="assets_vehicle_field_label">
    <span>{label}</span>
    {required ? <span className="assets_vehicle_field_required">*</span> : null}
  </span>
);

const pickOption = (options = [], value = "") =>
  (Array.isArray(options) ? options : []).find(
    (option) => String(option?.value ?? "") === String(value ?? "")
  ) || null;

const prependMissingOption = (options = [], nextOption = null) => {
  if (!nextOption?.value) return options;

  if (
    (options || []).some(
      (option) => String(option?.value ?? "") === String(nextOption?.value ?? "")
    )
  ) {
    return options;
  }

  return [nextOption, ...options];
};

const normalizeTripDropdownOption = (option = {}) => {
  const resolvedValue = option?.value ?? option?.id ?? "";
  const resolvedLabel =
    option?.label ??
    option?.trip_id ??
    option?.name ??
    (resolvedValue !== "" ? String(resolvedValue) : "");

  return {
    ...option,
    value: resolvedValue,
    label: resolvedLabel,
  };
};

const buildRouteMeta = (detail = {}) => {
  const startLocation = String(detail?.start_trip?.location || "").trim();
  const endLocation = String(detail?.end_trip?.location || "").trim();
  const fallbackRoute = String(detail?.route || "").trim();
  const routeLabel =
    [startLocation, endLocation].filter(Boolean).join(" \u2192 ") ||
    formatTripCostEstimationDisplayRoute(fallbackRoute);
  const route =
    [startLocation, endLocation].filter(Boolean).join(" > ") ||
    formatTripCostEstimationTableRoute(fallbackRoute);

  return {
    routeId: route || routeLabel || "",
    routeLabel,
    route,
  };
};

const buildCurrentOptions = (values = {}, tripDropdownOptions = []) => ({
  tripOptions: prependMissingOption(tripDropdownOptions, {
    value: values.tripId,
    label: values.tripLabel || String(values.tripId || "").trim() || "Trip",
  }),
  vehicleOptions: prependMissingOption([], {
    value: values.vehicleId,
    label: values.vehicleId || "Vehicle",
  }),
  driverOptions: prependMissingOption([], {
    value: values.driverId,
    label: values.driverName || values.driverId || "Driver",
    showIcon: true,
    image: values.driverImage || "",
  }),
  routeOptions: prependMissingOption([], {
    value: values.routeId,
    label: values.routeLabel || values.route || values.routeId || "Route",
    tableLabel: values.route || values.routeLabel || values.routeId || "Route",
  }),
});

const resetFetchedTripFieldTouches = (setFieldTouched) => {
  [
    "vehicleId",
    "driverId",
    "driverName",
    "routeId",
    "routeLabel",
    "distance",
    "tripDuration",
  ].forEach((fieldName) => {
    setFieldTouched(fieldName, false, false);
  });
};

const hasFetchedTripFieldValue = (fieldName, fieldValue) => {
  if (fieldName === "distance") {
    return Number(fieldValue) > 0;
  }

  return String(fieldValue ?? "").trim() !== "";
};

const syncFetchedTripFieldValidation = (
  setFieldTouched,
  validateForm,
  nextValues = {}
) => {
  [
    "vehicleId",
    "driverId",
    "routeId",
    "distance",
    "tripDuration",
  ].forEach((fieldName) => {
    setFieldTouched(
      fieldName,
      !hasFetchedTripFieldValue(fieldName, nextValues?.[fieldName]),
      false
    );
  });

  validateForm(nextValues);
};

const clearFetchedTripFields = (setFieldValue) => {
  setFieldValue("vehicleId", "", false);
  setFieldValue("driverId", "", false);
  setFieldValue("driverName", "", false);
  setFieldValue("driverImage", "", false);
  setFieldValue("routeId", "", false);
  setFieldValue("routeLabel", "", false);
  setFieldValue("route", "", false);
  setFieldValue("distance", "", false);
  setFieldValue("tripDuration", "", false);
};

const normalizeToArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value === null || value === undefined || value === "") return [];
  return [value];
};

const resolveUploadedFilePath = (fileEntry) => {
  if (typeof fileEntry === "string") return String(fileEntry).trim();
  if (!fileEntry || typeof fileEntry !== "object") return "";

  return String(
    fileEntry?.fileName ||
      fileEntry?.file_path ||
      fileEntry?.file_name ||
      fileEntry?.path ||
      fileEntry?.url ||
      fileEntry?.file ||
      fileEntry?.image ||
      (typeof fileEntry?.data === "string" ? fileEntry.data : "") ||
      fileEntry?.data?.fileName ||
      fileEntry?.data?.file_path ||
      fileEntry?.data?.file_name ||
      fileEntry?.data?.path ||
      fileEntry?.data?.url ||
      fileEntry?.data?.file ||
      fileEntry?.data?.image ||
      ""
  ).trim();
};

const CreateTripCostEstimation = () => {
  const navigate = useNavigate();
  const [tripDropdownOptions, setTripDropdownOptions] = useState([]);
  const [tripDropdownLoading, setTripDropdownLoading] = useState(false);
  const [tripDetailLoading, setTripDetailLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchTripDropdown = async () => {
      setTripDropdownLoading(true);

      try {
        const response = await apiService.getTripListsDropdown();
        const nextOptions = Array.isArray(response?.data)
          ? response.data.map(normalizeTripDropdownOption)
          : [];

        if (!isMounted) return;
        setTripDropdownOptions(nextOptions);
      } catch (error) {
        if (!isMounted) return;
        setTripDropdownOptions([]);
        errorNotify(error || "Failed to fetch trips dropdown");
      } finally {
        if (isMounted) {
          setTripDropdownLoading(false);
        }
      }
    };

    fetchTripDropdown();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const metrics = buildTripCostEstimationMetrics(values);
      const uploadedFiles = normalizeToArray(values.attachments)
        .map((file) => resolveUploadedFilePath(file))
        .filter(Boolean);
      const payload = new FormData();

      payload.append("trip_id", String(Number(values.tripId) || 0));
      payload.append("distance", String(Number(values.distance || 0)));
      payload.append("duration", String(values.tripDuration || "").trim());
      payload.append(
        "fuel_price_per_ltr",
        String(Number(values.fuelPricePerLiter || 0))
      );
      payload.append(
        "fuel_efficiency_per_ltr",
        String(Number(values.vehicleFuelEfficiency || 0))
      );
      payload.append("fuel_cost", String(metrics.estimatedFuelCost));
      payload.append(
        "maintenance_cost",
        String(metrics.estimatedMaintenanceCost)
      );
      payload.append("toll_cost", String(Number(values.estimatedTollCost || 0)));
      payload.append("driver_allowance", String(metrics.estimatedFoodCost));
      payload.append("estimated_total_cost", String(metrics.estimatedCost));
      payload.append(
        "estimated_total_revenue",
        String(Number(values.estimatedRevenue || 0))
      );
      payload.append("estimated_total_profit", String(metrics.projectedProfit));
      payload.append("note", String(values.notes || "").trim());
      uploadedFiles.forEach((file) => {
        payload.append("files[]", file);
      });

      await apiService.createTripCostEstimate(payload);

      successNotify("Trip cost estimation created successfully");
      navigate("/trip-cost-estimation");
    } catch (error) {
      errorNotify(error || "Failed to create trip cost estimation");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={getTripCostEstimationInitialValues()}
      validationSchema={createTripCostEstimationSchema}
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        setFieldValue,
        setFieldTouched,
        validateForm,
        handleSubmit,
        isSubmitting,
      }) => {
        const { tripOptions } = buildCurrentOptions(values, tripDropdownOptions);
        const selectedTripOption = pickOption(tripOptions, values.tripId);
        const updateAttachments = (updater) => {
          const nextFiles =
            typeof updater === "function" ? updater(values.attachments || []) : updater;
          setFieldValue("attachments", nextFiles);
          setFieldTouched("attachments", true, false);
        };

        const handleTripSelection = async (option) => {
          const nextTripId = option?.value ?? "";
          const nextTripLabel = option?.label ?? "";

          setFieldValue("tripId", nextTripId, false);
          setFieldValue("tripLabel", nextTripLabel, false);
          setFieldTouched("tripId", false, false);
          resetFetchedTripFieldTouches(setFieldTouched);
          clearFetchedTripFields(setFieldValue);

          if (!nextTripId) {
            return;
          }

          setTripDetailLoading(true);

          try {
            const response = await apiService.getTripEstimateDetail(nextTripId);
            const detail = response?.data || {};
            const routeMeta = buildRouteMeta(detail);

            setFieldValue(
              "tripLabel",
              String(detail?.trip_id || nextTripLabel || nextTripId).trim(),
              false
            );
            setFieldValue(
              "vehicleId",
              String(
                detail?.vehicle?.registration_number ||
                  detail?.vehicle?.name ||
                  detail?.vehicle?.id ||
                  ""
              ).trim(),
              false
            );
            setFieldValue(
              "driverId",
              String(
                detail?.primary_driver?.id ||
                  detail?.primary_driver?.user_id ||
                  ""
              ).trim(),
              false
            );
            setFieldValue(
              "driverName",
              String(
                detail?.primary_driver?.username ||
                  detail?.primary_driver?.name ||
                  ""
              ).trim(),
              false
            );
            setFieldValue(
              "driverImage",
              String(detail?.primary_driver?.image || "").trim(),
              false
            );
            setFieldValue("routeId", String(routeMeta.routeId || "").trim(), false);
            setFieldValue(
              "routeLabel",
              String(routeMeta.routeLabel || "").trim(),
              false
            );
            setFieldValue("route", String(routeMeta.route || "").trim(), false);
            setFieldValue("distance", detail?.distance ?? "", false);
            setFieldValue(
              "tripDuration",
              String(detail?.duration || "").trim(),
              false
            );

            syncFetchedTripFieldValidation(setFieldTouched, validateForm, {
              ...values,
              tripId: nextTripId,
              tripLabel: String(detail?.trip_id || nextTripLabel || nextTripId).trim(),
              vehicleId: String(
                detail?.vehicle?.registration_number ||
                  detail?.vehicle?.name ||
                  detail?.vehicle?.id ||
                  ""
              ).trim(),
              driverId: String(
                detail?.primary_driver?.id ||
                  detail?.primary_driver?.user_id ||
                  ""
              ).trim(),
              driverName: String(
                detail?.primary_driver?.username ||
                  detail?.primary_driver?.name ||
                  ""
              ).trim(),
              driverImage: String(detail?.primary_driver?.image || "").trim(),
              routeId: String(routeMeta.routeId || "").trim(),
              routeLabel: String(routeMeta.routeLabel || "").trim(),
              route: String(routeMeta.route || "").trim(),
              distance: detail?.distance ?? "",
              tripDuration: String(detail?.duration || "").trim(),
            });
          } catch (error) {
            clearFetchedTripFields(setFieldValue);
            syncFetchedTripFieldValidation(setFieldTouched, validateForm, {
              ...values,
              tripId: nextTripId,
              tripLabel: nextTripLabel,
              vehicleId: "",
              driverId: "",
              driverName: "",
              driverImage: "",
              routeId: "",
              routeLabel: "",
              route: "",
              distance: "",
              tripDuration: "",
            });
            errorNotify(error || "Failed to fetch trip estimate detail");
          } finally {
            setTripDetailLoading(false);
          }
        };

        const disableSubmit =
          isSubmitting || tripDropdownLoading || tripDetailLoading;

        return (
          <div className="create_vehicle_page trip_cost_estimation_create_page">
            <Breadcrumbs values={["Finance", "Trip Cost Estimation", "Create Estimation"]} />

            <div className="page_header access_view">
              <h2>Create Estimation</h2>

              <div className="d-flex align-items-center gap-2">
                <Button
                  cancelBtn={() => navigate("/trip-cost-estimation")}
                  submitBtn={handleSubmit}
                  submitText={isSubmitting ? "Saving..." : "Save"}
                  type="button"
                  shouldRow={false}
                  loading={isSubmitting}
                  disabled={disableSubmit}
                />
              </div>
            </div>

            <div className="page_container">
              <Row className="driver_page_layout trip_cost_estimation_create_layout">
                <Col lg={9}>
                  <div className="trip_information_main_div driver_form_shell trip_cost_estimation_create_shell">
                    <h5>Trip Cost Estimation Creation</h5>

                    <div className="driver_form_content">
                      <div className="driver_form_section">
                        <div className="driver_form_section_header">
                          <h3>Trip Selection</h3>
                          <p>Main information for this part record.</p>
                        </div>

                        <Row>
                          <Col md={6}>
                            <Select
                              label={renderFieldLabel("Trip ID")}
                              placeholder="Select Trip ID"
                              options={tripOptions}
                              loading={tripDropdownLoading}
                              value={selectedTripOption}
                              name="tripId"
                              onChange={handleTripSelection}
                              onBlur={handleBlur}
                              error={errors.tripId}
                              touched={touched.tripId}
                              isSearch
                            />
                          </Col>
                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("Vehicle")}
                              type="text"
                              placeholder="Vehicle will auto-fill"
                              name="vehicleId"
                              value={values.vehicleId}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.vehicleId}
                              touched={touched.vehicleId}
                              disabled
                            />
                          </Col>
                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("Driver")}
                              type="text"
                              placeholder="Driver will auto-fill"
                              name="driverName"
                              value={values.driverName}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.driverId}
                              touched={touched.driverId}
                              disabled
                            />
                          </Col>
                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("Route")}
                              type="text"
                              placeholder="Route will auto-fill"
                              name="routeLabel"
                              value={values.routeLabel || values.route}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.routeId}
                              touched={touched.routeId}
                              disabled
                            />
                          </Col>
                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("Distance")}
                              type="number"
                              placeholder="Distance will auto-fill"
                              name="distance"
                              value={values.distance}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.distance}
                              touched={touched.distance}
                              showExtraText
                              extraText="KM"
                              disabled
                            />
                          </Col>
                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("Trip Duration")}
                              type="text"
                              placeholder="Trip duration will auto-fill"
                              name="tripDuration"
                              value={values.tripDuration}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.tripDuration}
                              touched={touched.tripDuration}
                              disabled
                            />
                          </Col>
                        </Row>
                      </div>

                      <div className="driver_form_section">
                        <div className="driver_form_section_header">
                          <h3>Cost Estimation Inputs</h3>
                          <p>Main information for this part record.</p>
                        </div>

                        <Row>
                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("Fuel Price Per Liter")}
                              type="number"
                              placeholder="Enter Fuel Price"
                              name="fuelPricePerLiter"
                              value={values.fuelPricePerLiter}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.fuelPricePerLiter}
                              touched={touched.fuelPricePerLiter}
                              showExtraText
                              extraText="PKR"
                            />
                          </Col>
                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("Vehicle Fuel Efficiency per Liter")}
                              type="number"
                              placeholder="Enter Fuel Efficiency"
                              name="vehicleFuelEfficiency"
                              value={values.vehicleFuelEfficiency}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.vehicleFuelEfficiency}
                              touched={touched.vehicleFuelEfficiency}
                              showExtraText
                              extraText="KM"
                            />
                          </Col>
                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("Driver Allowance")}
                              type="number"
                              placeholder="Enter Driver Allowance"
                              name="foodAllowancePerDay"
                              value={values.foodAllowancePerDay}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.foodAllowancePerDay}
                              touched={touched.foodAllowancePerDay}
                              showExtraText
                              extraText="PKR"
                            />
                          </Col>
                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("Maintenance Cost per KM")}
                              type="number"
                              placeholder="Enter Maintenance Cost"
                              name="maintenanceCostPerKm"
                              value={values.maintenanceCostPerKm}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.maintenanceCostPerKm}
                              touched={touched.maintenanceCostPerKm}
                              showExtraText
                              extraText="PKR"
                            />
                          </Col>
                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("Estimated Toll Cost")}
                              type="number"
                              placeholder="Enter Toll Cost"
                              name="estimatedTollCost"
                              value={values.estimatedTollCost}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.estimatedTollCost}
                              touched={touched.estimatedTollCost}
                              showExtraText
                              extraText="PKR"
                            />
                          </Col>
                        </Row>
                      </div>

                      <div className="driver_form_section">
                        <div className="driver_form_section_header">
                          <h3>Estimated Revenue</h3>
                          <p>Main information for this part record.</p>
                        </div>

                        <Row>
                          <Col md={12}>
                            <Input
                              label={renderFieldLabel("Estimated Revenue")}
                              type="number"
                              placeholder="Enter Revenue"
                              name="estimatedRevenue"
                              value={values.estimatedRevenue}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.estimatedRevenue}
                              touched={touched.estimatedRevenue}
                              showExtraText
                              extraText="PKR"
                            />
                          </Col>
                          <Col md={12}>
                            <div className="issue_file_upload_wrapper">
                              <label>Attachment</label>
                              <FileUpload
                                destination="trip-cost-estimation"
                                files={values.attachments || []}
                                setFiles={updateAttachments}
                                maxFileSize={10}
                                className="issue_file_upload create_vehicle_upload"
                              />
                            </div>
                          </Col>
                        </Row>
                      </div>
                    </div>
                  </div>
                </Col>

                <Col lg={3}>
                  <div className="driver_form_sidebar trip_cost_estimation_create_sidebar">
                    <div className="trip_notes_main_div create_vehicle_notes driver_notes_card trip_cost_estimation_notes_card">
                      <h5>Notes</h5>
                      <ReactQuill
                        placeholder="Notes if any..."
                        modules={modules}
                        formats={formats}
                        value={values.notes || ""}
                        onChange={(content) => setFieldValue("notes", content)}
                        className="react_quill_input notes_quill_input quill-small-font trip_quill_input"
                      />
                      {touched.notes && errors.notes ? (
                        <div
                          className="error-message"
                          style={{
                            color: "#ff0000de",
                            fontSize: "12px",
                            margin: "8px 12px 0",
                          }}
                        >
                          {errors.notes}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        );
      }}
    </Formik>
  );
};

export default CreateTripCostEstimation;
